import { createContext } from '@shared/utils/createContext';
import PrintPriceCalculator from '@shared/utils/print-price/PrintPriceCalculator';
import { StationeryProduct } from '@shared/utils/print-price/types';
import React, { ReactNode, useMemo, useRef } from 'react';
import { useProductToUIMapping } from '../useProductToUIMapping';
import { Product } from '../product.types';
import { useProductAttributeStates } from './state/useProductAttributeStates';
import { usePrintPrices } from '@apps/card/hooks/usePrintPrices';
import { parseOrderCurrencyConversionData } from '@apps/card/utils/orderCurrencyConversion';
import { CurrencyCode } from '@shared/hooks/useGeoLocale';

type StationeryProductPricingContextType = {
  products: StationeryProduct[];
  activeProduct: StationeryProduct & Product;
  priceCalculator: PrintPriceCalculator;
  attributeStates: ReturnType<typeof useProductAttributeStates>;
  orderCurrencyISO4217?: CurrencyCode;
};

const [Provider, useStationeryProductPricingContext] = createContext<StationeryProductPricingContextType>({
  name: 'StationeryProductPricingContext',
});

type StationeryProductPricingContextProviderProps = {
  products: StationeryProduct[];
  activeProductId: string;
  children: ReactNode;
  orderCurrencyConversionData?: unknown;
  orderCurrencyISO4217?: string | null;
};

const StationeryProductPricingContextProvider = (props: StationeryProductPricingContextProviderProps) => {
  const { products, children, activeProductId, orderCurrencyConversionData, orderCurrencyISO4217 } = props;
  const { getProductWithUIMapping } = useProductToUIMapping();

  // Parse order currency conversion data if provided
  const parsedConversionRate = useMemo(() => {
    return parseOrderCurrencyConversionData(orderCurrencyConversionData);
  }, [orderCurrencyConversionData]);

  const priceCalculator = usePrintPrices(products, parsedConversionRate);
  const activeProduct = products.find(p => p.id === activeProductId);
  const attributeStates = useProductAttributeStates();
  const previousActiveProduct = useRef(activeProductId);

  // Destroy Attribute State to Draft State connections when switching products
  if (activeProductId !== previousActiveProduct.current) {
    previousActiveProduct.current = activeProductId;
    attributeStates.destroyLinks();
  }

  if (!activeProduct) {
    throw new Error('Product not avaliable');
  }
  const mappedActiveProduct = getProductWithUIMapping(activeProduct);

  if (!mappedActiveProduct) {
    throw new Error('Product config not defined');
  }

  return (
    <Provider
      value={{
        products,
        activeProduct: mappedActiveProduct,
        priceCalculator,
        attributeStates,
        orderCurrencyISO4217: orderCurrencyISO4217 as CurrencyCode | undefined,
      }}
    >
      {children}
    </Provider>
  );
};

export { StationeryProductPricingContextProvider, useStationeryProductPricingContext };
